import React, { createContext, useContext, useState, useMemo, useCallback, ReactNode } from 'react';
import GlobalLoading from '@components/common/loading/GlobalLoading';

interface LoadingContextType {
  isGlobalLoading: boolean;
  loadingMessage: string;
  showGlobalLoading: (message?: string) => void;
  hideGlobalLoading: () => void;
  withGlobalLoading: <T>(operation: () => Promise<T>, message?: string) => Promise<T>;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

interface LoadingProviderProps {
  children: ReactNode;
}

export const LoadingProvider: React.FC<LoadingProviderProps> = ({ children }) => {
  const [isGlobalLoading, setIsGlobalLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Processing...');

  const showGlobalLoading = useCallback((message = 'Processing...') => {
    setLoadingMessage(message);
    setIsGlobalLoading(true);
  }, []);

  const hideGlobalLoading = useCallback(() => {
    setIsGlobalLoading(false);
  }, []);

  const withGlobalLoading = useCallback(
    async <T,>(operation: () => Promise<T>, message = 'Processing...'): Promise<T> => {
      try {
        showGlobalLoading(message);
        const result = await operation();
        return result;
      } finally {
        hideGlobalLoading();
      }
    },
    [showGlobalLoading, hideGlobalLoading]
  );

  const value: LoadingContextType = useMemo(
    () => ({
      isGlobalLoading,
      loadingMessage,
      showGlobalLoading,
      hideGlobalLoading,
      withGlobalLoading
    }),
    [isGlobalLoading, loadingMessage, showGlobalLoading, hideGlobalLoading, withGlobalLoading]
  );

  return (
    <LoadingContext.Provider value={value}>
      {children}
      {isGlobalLoading && <GlobalLoading message={loadingMessage} />}
    </LoadingContext.Provider>
  );
};

export const useLoading = (): LoadingContextType => {
  const context = useContext(LoadingContext);
  if (context === undefined) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};

export default LoadingContext;
